/**
 * PhotoSwipe lightbox.
 *
 * Ported from benpazdernik.com, which is the build reference for this rework.
 *
 * Belongs in the bundle rather than in an inline <script> in the page. The
 * inline version only ran on a full page load — after any page transition the
 * module never executed, so clicking a photo fell through to the href and
 * opened a new tab. That is why the lightbox worked "sometimes" there.
 *
 * PhotoSwipe is loaded on demand. The specifier is built at runtime so esbuild
 * leaves the import external, which keeps ~30 KB off every page with no
 * gallery. Root-absolute because a dynamic import() resolves against the
 * module's own URL (/assets/scripts/), not against the document's <base href>.
 *
 * Opt in from markup: `data-lightbox="<child selector>"` on the gallery
 * container. Every container gets its own instance, which is what keeps the
 * travel index from paging out of one gallery and into the next.
 *
 * The strings all arrive as data attributes on that container, written by
 * 03_Galerie from the lexicon. PhotoSwipe falls back to its own English for
 * any option that is not a string, so a missing attribute is not an error --
 * it is "Close" and "Next" in a German document, which is what a11y finding
 * L1 was.
 *
 * Torn down on a page transition, and the pending import is guarded. The
 * import is what makes the guard necessary: it is asynchronous, so a visitor
 * who clicks a link while it is still in flight would have its `then` run
 * against a gallery Swup has already replaced — attaching a lightbox to a
 * detached element, and leaving behind an instance nothing will ever destroy.
 */

const VENDOR = '/assets/vendor/photoswipe/'

/* `inert` needs Safari 15.5 and this bundle targets 14.5 — the same floor
   src/js/nav.js works against, and the same reasoning: below it PhotoSwipe's
   own `focusin` handler is the whole protection. That handler does not wrap
   Tab around the dialog; it catches focus that has ALREADY landed outside and
   pulls it back. Measured: the Tab past the last control goes nowhere for one
   press, and the press after that touches a link in the page behind before
   being returned. `inert` is what removes that page from the tab order and
   from the accessibility tree in the first place. */
const supportsInert = 'inert' in HTMLElement.prototype

/* Everything the open lightbox covers. PhotoSwipe appends its element to
   <body>, so the page is exactly that element's siblings. */
const background = (pswpElement) =>
  [...document.body.children].filter((node) => node !== pswpElement)

/**
 * Padding by viewport, not one figure for all of them.
 *
 * @decision Padding rather than `initialZoomLevel`. The zoom option can be made
 *           to fill the screen too, but PhotoSwipe then counts the slide as
 *           zoomed in — `isPannable()` is literally `currZoomLevel >
 *           zoomLevels.fit` — and a horizontal drag pans the photograph instead
 *           of paging to the next one until it reaches the edge. Measured at
 *           375px: the image did come out full-width, and it also picked up
 *           ±70px of pan that ate the swipe. Padding moves `fit` itself, so the
 *           opening state stays the un-zoomed one and the carousel keeps the
 *           gesture.
 *
 * Width alone decides it. A sideways phone (812×375) looks like it wants the
 * narrow set too, but on a screen that short every frame comes out
 * height-constrained, so the side padding never binds and the branch changes
 * nothing — while a short desktop window caught by the same test would lose
 * margin it was meant to have.
 */
const paddingFn = ({ x }) =>
  x < 768
    ? { top: 60, bottom: 12, left: 8, right: 8 }
    : { top: 30, bottom: 30, left: 70, right: 70 }

export function initLightbox (root = document) {
  const galleries = root.querySelectorAll('[data-lightbox]')
  if (galleries.length === 0) return

  /* The teardown can run before the import resolves, so it cannot just close
     over a list of instances that does not exist yet. `torn` is what both
     halves read. */
  let torn = false
  const instances = []

  Promise.all([
    import(VENDOR + 'photoswipe-lightbox.esm.min.js'),
    import(VENDOR + 'photoswipe.esm.min.js')
  ]).then(([lightboxModule, coreModule]) => {
    if (torn) return

    galleries.forEach((gallery) => {
      const strings = gallery.dataset

      const instance = new lightboxModule.default({
        gallery,
        children: gallery.dataset.lightbox,
        pswpModule: coreModule.default,
        paddingFn,
        // Set here rather than in CSS: PhotoSwipe writes this onto `.pswp__bg`
        // as an inline style, which no stylesheet can outrank.
        //
        // This and the blur radius in _gallery.scss pull against each other:
        // the more opaque the white, the less backdrop is left for the blur to
        // act on visibly. 0.7 left a whole gallery of thumbnails readable
        // behind the photograph, which on a phone reads as clutter rather than
        // as depth. At 0.85 the page is a quiet wash and the blur is what stops
        // its edges from showing through as recognisable shapes.
        bgOpacity: 0.85,
        closeTitle: strings.lightboxClose,
        zoomTitle: strings.lightboxZoom,
        arrowPrevTitle: strings.lightboxPrev,
        arrowNextTitle: strings.lightboxNext,
        errorMsg: strings.lightboxError
      })

      let live = null
      let inerted = []
      let seenIndex = null
      let returnTo = null

      instance.on('afterInit', () => {
        const element = instance.pswp && instance.pswp.element
        if (!element) return

        /* `role="dialog"` is PhotoSwipe's. The other two are not, and without
           them the dialog is announced as a bare "dialog" and a screen
           reader's virtual cursor is free to walk out of it into the page
           behind. */
        element.setAttribute('aria-modal', 'true')
        if (strings.lightboxLabel) {
          element.setAttribute('aria-label', strings.lightboxLabel)
        }

        if (supportsInert) {
          /* Read before the flags go on: an element loses focus the instant an
             ancestor turns inert. PhotoSwipe restores focus itself only when
             IT moved focus in, which it does not do when the lightbox was
             opened by pointer -- `_wasFocused` stays false and its own
             `returnFocus` is skipped. Without this, clicking a thumbnail and
             pressing Escape drops the reader at the top of the document, which
             on a 55-image gallery is a long way from where they were. */
          const active = document.activeElement
          returnTo = active && active !== document.body && !element.contains(active)
            ? active
            : null

          inerted = background(element)
          for (const node of inerted) node.inert = true
        }

        /* The slide announcement. PhotoSwipe's counter is not a live region
           and the focus does not move when the slide changes, so paging is
           silent — position and subject both.

           Inside .pswp, not in the page: the background goes inert above, and
           an inert subtree is out of the accessibility tree, so a region left
           out there would never be read. That is also why this one is built
           here rather than rendered empty into the document the way the
           Sortiment filter's is. */
        live = document.createElement('p')
        live.className = 'pswp__live'
        live.setAttribute('role', 'status')
        element.appendChild(live)
      })

      /* Only a real move is announced. The first `change` fires while the
         dialog is still opening, when the region has only just been inserted
         and is not yet being watched — its text would be dropped, and the
         dialog's own name is what the user hears at that moment anyway.
         Keyed on the index rather than on a counter, so it does not matter how
         many times PhotoSwipe dispatches `change` while it opens. */
      instance.on('change', () => {
        const pswp = instance.pswp
        if (!pswp) return

        const index = pswp.currIndex
        const moved = seenIndex !== null && index !== seenIndex
        seenIndex = index

        if (!moved || !live || !strings.lightboxPosition) return

        const slide = pswp.currSlide
        live.textContent = strings.lightboxPosition
          .replace('{index}', String(index + 1))
          .replace('{total}', String(pswp.getNumItems()))
          .replace('{alt}', (slide && slide.data && slide.data.alt) || '')
      })

      /* The flags come off on `close`, not on `destroy`. PhotoSwipe restores
         focus to the thumbnail from its own `destroy` handler, and an inert
         subtree cannot take focus -- clear them any later and `.focus()` is a
         silent no-op that drops the keyboard user on <body>, back at the top
         of the page. Measured: that is exactly what this did when the clear
         sat in `destroy`. */
      /* Two events, and the split matters. The flags come off on `close`,
         because PhotoSwipe restores focus from its own `destroy` handler and
         an inert subtree cannot take focus -- clear them any later and that
         `.focus()` is a silent no-op.

         Our own restore has to wait for `destroy` all the same. Measured:
         called during `close` it runs, and the closing sequence that follows
         drops the focus again before it settles; called from `destroy` it
         holds. */
      instance.on('close', () => {
        for (const node of inerted) node.inert = false
        inerted = []
      })

      instance.on('destroy', () => {
        /* Only when the flags are what dropped it. On the keyboard path
           PhotoSwipe has already put focus back on the same element by now and
           this does nothing; when the lightbox was opened by pointer it never
           moved focus in, so `returnFocus` is skipped and this is the only
           thing that returns the reader to the thumbnail they clicked. */
        if (returnTo && document.activeElement === document.body) {
          returnTo.focus()
        }

        returnTo = null
        live = null
        seenIndex = null
      })

      instance.init()
      instances.push(instance)
    })
  })

  return () => {
    torn = true
    /* `destroy`, not `close`: close would leave the instance bound to a gallery
       that is about to be removed. Going through destroy also runs the handler
       above, which is what returns focus and clears the inert flags if the
       lightbox happens to be open at the moment the visitor navigates. */
    instances.forEach((instance) => instance.destroy())
    instances.length = 0
  }
}
